import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Cell,
} from 'recharts'
import * as React from 'react'

const dataset = [
  { year: 2014, actual: 11.39 },
  { year: 2015, actual: -0.73 },
  { year: 2016, actual: 9.54 },
  { year: 2017, actual: 19.42 },
  { year: 2018, actual: -6.24 },
  { year: 2019, actual: 28.88 },
  { year: 2020, actual: 16.26 },
  { year: 2021, actual: 26.89 },
  { year: 2022, actual: -19.44 },
  { year: 2023, actual: 24.23 },
  { year: 2024, actual: 23.31 },
]

export default function ActualBarChart({ strategy = 'none', rate = 8.8 }) {
  const [visibleYearCount, setVisibleYearCount] = React.useState(11)

  const strategies = {
    capRate: (actual) => {
      if (actual > 0) return { base: rate, extra: actual - rate }
      return { base: 0, extra: actual }
    },
    fixedRate: (actual) => ({ base: rate, extra: 0 }),
    participation: (actual) => ({
      base: actual > 0 ? actual * rate : 0,
      extra: actual > 0 ? actual * rate : actual,
    }),
    trigger: (actual) => ({ base: actual > 0 ? rate : 0, extra: 0 }),
    none: (actual) => ({ base: actual, extra: 0 }),
  }

  const compute = strategies[strategy] || strategies.none

  const transformed = dataset.map((d) => {
    const { base, extra } = compute(d.actual)
    return { ...d, base, extra }
  })

  const getColor = (value) =>
    value > 0 ? '#6aa84f' : value < 0 ? '#cc0100' : '#afafaf'

  const formatLabel = (value) => {
    if (value === 0) return '+0%'
    if (value > 0) return `+${value.toFixed(1)}%`
    return `${value.toFixed(1)}%`
  }
  const visibleData = transformed.map((d, i) => {
    if (i < visibleYearCount) return d
    return { ...d, base: 0, extra: 0 }
  })

  return (
    <>
      <ResponsiveContainer width="100%" height={500}>
        <BarChart
          barSize={60}
          data={visibleData}
          margin={{ top: 20, right: 30, left: 10, bottom: 80 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="year"
            angle={-55}
            textAnchor="end"
            interval={0}
            height={70}
          />
          <YAxis tickFormatter={(v) => `${v.toFixed(1)}%`} domain={[-40, 40]} />
          <Tooltip
            formatter={(value) => `${value.toFixed(1)}%`}
            labelFormatter={(label) => `Year ${label}`}
          />

          {strategy === 'none' ? (
            <Bar dataKey="actual" radius={[4, 4, 0, 0]}>
              {visibleData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getColor(entry.actual)} />
              ))}
              <LabelList
                dataKey="actual"
                position="center"
                formatter={formatLabel}
                style={{ fill: 'white', fontWeight: 600, fontSize: 14 }}
              />
            </Bar>
          ) : (
            <>
              <Bar dataKey="base" stackId="a" fill="#6aa84f">
                {visibleData.map((entry, index) => (
                  <Cell
                    key={`cell-base-${index}`}
                    fill="#6aa84f"
                    radius={entry.extra === 0 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  />
                ))}
                <LabelList
                  dataKey="base"
                  content={({
                    x = 0,
                    y = 0,
                    width = 0,
                    height = 0,
                    value,
                    index,
                  }) => {
                    if (value == null || visibleYearCount <= index) return null

                    const label =
                      value === 0
                        ? '+0%'
                        : value > 0
                        ? `+${value.toFixed(1)}%`
                        : `${value.toFixed(1)}%`
                    const fillColor = value === 0 ? 'blue' : 'white'
                    const textY = y + height - 6

                    return (
                      <text
                        x={x + width / 2}
                        y={textY}
                        textAnchor="middle"
                        dominantBaseline="auto"
                        fill={fillColor}
                        fontSize={14}
                        fontWeight={600}
                      >
                        {label}
                      </text>
                    )
                  }}
                />
              </Bar>
              <Bar
                dataKey="extra"
                stackId="a"
                fill="#afafaf"
                radius={[4, 4, 0, 0]}
              />
            </>
          )}
        </BarChart>
      </ResponsiveContainer>

      <div
        style={{
          textAlign: 'center',
          margin: '-60px 0 60px',
          zIndex: 1,
          position: 'relative',
        }}
      >
        <button
          onClick={() =>
            setVisibleYearCount((c) => Math.min(c + 1, transformed.length))
          }
          disabled={visibleYearCount === transformed.length}
        >
          Next
        </button>
        <button
          onClick={() => setVisibleYearCount(1)}
          disabled={visibleYearCount === 1}
          style={{ marginLeft: 8 }}
        >
          Clear
        </button>
      </div>
    </>
  )
}
